import React, {useEffect, useMemo, useState} from 'react';

import {Badge, TextInput} from '@mantine/core';
import {Table} from 'src/design/atoms/Table';
import {Box} from 'src/design/atoms/Box';
import {Collapse} from 'src/design/atoms/Collapse';
import {Group} from 'src/design/atoms/Group';
import {Text} from 'src/design/atoms/Text';
import {UnstyledButton} from 'src/design/atoms/UnstyledButton';
import {IconChevronDown, IconChevronRight, IconSearch} from '@tabler/icons-react';
import type {
  AuditChildChangeResponse} from 'src/api/ProactApiClient';
import {
  FormFieldTypeEnum,
} from 'src/api/ProactApiClient';
import {DiffTable} from 'src/design/molecules/DiffTable';
import {DiffCell} from 'src/design/atoms/DiffCell';
import {IconSize} from 'src/design/atoms/icons/CommonIcons';
import {auditValuesEqual, formatAuditValue} from 'src/shared/utils/auditValue';

const COLLAPSE_THRESHOLD = 5;
const LONG_TEXT_CHAR_LIMIT = 400;

// Field types that render only a "Changed" indicator (binary/opaque data)
const BINARY_FIELD_TYPES = new Set([
  FormFieldTypeEnum.Signature,
  FormFieldTypeEnum.DrawingPad,
  FormFieldTypeEnum.FileUpload,
]);

// Field types that use text diff rendering (multiline-friendly truncation)
const TEXT_DIFF_FIELD_TYPES = new Set([
  FormFieldTypeEnum.Text,
  FormFieldTypeEnum.RichText,
  FormFieldTypeEnum.Paragraph,
]);

interface TruncatedValueProps {
  value?: string | null;
  side: 'original' | 'current';
}

const TruncatedValue: React.FC<TruncatedValueProps> = ({value, side}) => {
  const [expanded, setExpanded] = useState(false);
  const text = formatAuditValue(value);
  const isLong = text.length > LONG_TEXT_CHAR_LIMIT;

  if (!isLong) {
    return <>{text}</>;
  }

  return (
    <>
      {expanded ? text : `${text.slice(0, LONG_TEXT_CHAR_LIMIT)}…`}
      <UnstyledButton
        onClick={() => setExpanded(v => !v)}
        style={{display: 'block', marginTop: 4}}
        aria-label={expanded ? `Collapse ${side} value` : `Show full ${side} value`}>
        <Text size="xs" c="blue">
          {expanded ? 'Show less' : 'Show full value'}
        </Text>
      </UnstyledButton>
    </>
  );
};

interface ChildRowProps {
  child: AuditChildChangeResponse;
}

// Renders a single audit child-change row. fieldType is only set for Form
// field rows — UserGroup permission rows (and any other non-Form callers)
// leave it undefined and fall through to the plain side-by-side diff cells.
const ChildRow: React.FC<ChildRowProps> = ({child}) => {
  const {label, fieldType, original, current} = child;
  const isDifferent = !auditValuesEqual(original, current);

  if (fieldType != null && BINARY_FIELD_TYPES.has(fieldType)) {
    return (
      <Table.Tr>
        <Table.Td>
          <Text>{label}</Text>
        </Table.Td>
        <Table.Td colSpan={2}>
          <Badge color="yellow" variant="outline">
            Changed
          </Badge>
        </Table.Td>
      </Table.Tr>
    );
  }

  const useTruncation = fieldType != null && TEXT_DIFF_FIELD_TYPES.has(fieldType);

  return (
    <Table.Tr>
      <Table.Td>
        <Text>{label}</Text>
      </Table.Td>
      <DiffCell side="original" isDifferent={isDifferent}>
        {useTruncation ? (
          <TruncatedValue value={original} side="original" />
        ) : (
          formatAuditValue(original)
        )}
      </DiffCell>
      <DiffCell side="current" isDifferent={isDifferent}>
        {useTruncation ? (
          <TruncatedValue value={current} side="current" />
        ) : (
          formatAuditValue(current)
        )}
      </DiffCell>
    </Table.Tr>
  );
};

interface ChildChangeSectionProps {
  group: string;
  /** Items for this group after the showChangesOnly filter, sorted. Stable across search keystrokes. */
  allItems: AuditChildChangeResponse[];
  /** allItems further narrowed by the active search query. */
  filteredItems: AuditChildChangeResponse[];
  hasSearch: boolean;
}

const ChildChangeSection: React.FC<ChildChangeSectionProps> = ({
  group,
  allItems,
  filteredItems,
  hasSearch,
}) => {
  const [collapsed, setCollapsed] = useState(
    () => allItems.length > COLLAPSE_THRESHOLD
  );
  // Reset collapse state whenever this group's underlying item set changes
  // identity (e.g., user opens a different audit row in a list).
  useEffect(() => {
    setCollapsed(allItems.length > COLLAPSE_THRESHOLD);
     
  }, [allItems]);
  const effectiveCollapsed = collapsed && !hasSearch;

  return (
    <Box mb="md">
      <UnstyledButton
        onClick={() => setCollapsed(v => !v)}
        aria-expanded={!effectiveCollapsed}
        aria-label={`Toggle ${group} section`}>
        <Group gap="xs" mb="xs">
          {effectiveCollapsed ? (
            <IconChevronRight size={IconSize.md} />
          ) : (
            <IconChevronDown size={IconSize.md} />
          )}
          <Text fw={600} size="sm">
            {group}
          </Text>
          <Badge color="blue" size="sm">
            {filteredItems.length}
          </Badge>
        </Group>
      </UnstyledButton>

      <Collapse in={!effectiveCollapsed}>
        <DiffTable>
          {filteredItems.length === 0 ? (
            <Table.Tr>
              <Table.Td colSpan={3}>
                <Text c="dimmed" ta="center" size="sm">
                  No matching items
                </Text>
              </Table.Td>
            </Table.Tr>
          ) : (
            filteredItems.map(child => (
              <ChildRow key={child.key} child={child} />
            ))
          )}
        </DiffTable>
      </Collapse>
    </Box>
  );
};

export interface AuditChildChangesProps {
  childChanges: AuditChildChangeResponse[];
  showChangesOnly: boolean;
}

export const AuditChildChanges: React.FC<AuditChildChangesProps> = ({
  childChanges,
  showChangesOnly,
}) => {
  const [search, setSearch] = useState('');

  // Group by `group`, sort within each group, and apply showChangesOnly.
  // Memoized independent of `search` so collapse state isn't disturbed by typing.
  const groupedSections = useMemo(() => {
    const byGroup = new Map<string, AuditChildChangeResponse[]>();
    childChanges.forEach(child => {
      const list = byGroup.get(child.group) ?? [];
      list.push(child);
      byGroup.set(child.group, list);
    });

    return Array.from(byGroup.entries()).map(([group, items]) => {
      const sorted = [...items].sort((a, b) => {
        const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
        const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
        if (orderA !== orderB) return orderA - orderB;
        return a.key.localeCompare(b.key);
      });
      const visible = showChangesOnly
        ? sorted.filter(c => !auditValuesEqual(c.original, c.current))
        : sorted;
      return {group, items: visible};
    });
  }, [childChanges, showChangesOnly]);

  const query = search.trim().toLowerCase();
  const sections = useMemo(
    () =>
      groupedSections.map(({group, items}) => ({
        group,
        allItems: items,
        filteredItems: query
          ? items.filter(c => c.label.toLowerCase().includes(query))
          : items,
      })),
    [groupedSections, query]
  );

  if (!childChanges.length) {
    return null;
  }

  return (
    <Box>
      <Group justify="end" mb="xs">
        <TextInput
          placeholder="Filter by name..."
          leftSection={<IconSearch size={IconSize.md} />}
          value={search}
          onChange={e => setSearch(e.target.value)}
          size="xs"
          w={220}
          aria-label="Filter audit changes"
        />
      </Group>

      {sections.map(section => (
        <ChildChangeSection
          key={section.group}
          group={section.group}
          allItems={section.allItems}
          filteredItems={section.filteredItems}
          hasSearch={!!query}
        />
      ))}
    </Box>
  );
};
